import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut,
  Wifi,
  WifiOff,
  Pencil,
  FileText,
  Trash2,
  Loader2,
  Search,
  HelpCircle,
  RefreshCw,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/index.js";
import {
  listInformes,
  deleteInforme,
  prewarmCatalogs,
  TIPO_LABEL,
  type EstadoInforme,
  type Informe,
  type InformeFiltros,
  type TipoInforme,
} from "@/features/reports/index.js";
import {
  getAllReports,
  deleteReport,
  getReportByServerId,
  resetInflight,
  flushRun,
  cleanupGhostReports,
  cleanupOrphanOutboxItems,
  requeueOrphanEnColaReports,
  discardTerminalFailed,
  subscribeToFlush,
  mirrorServerReports,
  prefetchOwnPdfs,
  // Fase 3: backoff + terminal state
  resetFailedItems,
  resetErrorBackoff,
  // Fase 3: LRU startup prune
  pruneOnStartup,
} from "@/features/offline-sync/index.js";
import { SyncStatusPanel } from "./SyncStatusPanel.js";
import { StatusBadge } from "@/shared/ui/atoms/StatusBadge.js";
import { TipoBadge } from "@/shared/ui/atoms/TipoBadge.js";
import { resolveDisplayEstado } from "./estado-display.js";
import { Avatar } from "@/shared/ui/atoms/Avatar.js";
import { AppShell } from "@/shared/ui/AppShell.js";
import { Button, buttonVariants } from "@/shared/ui/button.js";
import { Input } from "@/shared/ui/input.js";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog.js";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover.js";
import { BrandLogo } from "@/shared/ui/BrandLogo.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select.js";
import { friendlyMessage } from "@/shared/lib/errors.js";
import { cn } from "@/shared/lib/utils.js";
import { getLastSync, setLastSync } from "@/shared/lib/last-sync.js";

const labelClass =
  "font-mono text-[0.625rem] font-medium uppercase tracking-[0.18em] text-muted-foreground";

const thClass = "px-4 py-3 font-mono text-[0.625rem] font-medium uppercase tracking-[0.12em]";

const TIPOS: TipoInforme[] = ["IE", "IS", "AC"];
const ESTADOS: EstadoInforme[] = ["borrador", "en_cola", "sincronizado", "enviado"];
const ESTADO_LABEL: Record<EstadoInforme, string> = {
  borrador: "Borrador",
  en_cola: "En cola",
  sincronizado: "Sincronizado",
  enviado: "Enviado",
};

/** Clase base compartida por los botones/links de la columna Acciones — compacta, una sola fila. */
const accionClass =
  "h-8 gap-1 px-2.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] whitespace-nowrap";

/** Color sólido del punto de estado (espejo de StatusBadge). */
const ESTADO_DOT: Record<EstadoInforme, string> = {
  borrador: "var(--estado-borrador)",
  en_cola: "var(--estado-en-cola)",
  sincronizado: "var(--estado-sincronizado)",
  enviado: "var(--estado-enviado)",
};

/** Qué puede hacer el técnico en cada estado — alimenta la leyenda de ayuda. */
const ESTADO_AYUDA: Record<EstadoInforme, string> = {
  borrador: "En edición. Puedes continuar editándolo o eliminarlo.",
  en_cola: "Listo, esperando sincronizar. Se puede ver el detalle cuando esté sincronizado.",
  sincronizado:
    "Subido al servidor. Solo lectura — puedes ver el detalle, el PDF y enviarlo desde ahí.",
  enviado: "Entregado al cliente. Ver detalle para descargar el PDF.",
};

/** Descripciones por tipo para las tarjetas «¿Qué levantamos hoy?». */
const TIPO_DESC: Record<TipoInforme, string> = {
  IE: "Evaluación de estado de equipos: fotos, diagnóstico y recomendaciones.",
  IS: "Reporte de servicio en campo: fotos, actividades, tipo sistema y conclusiones.  ",
  AC: "Acta de conformidad del cliente, necesita firma digital.",
};

function formatFecha(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("es-PE");
}

/**
 * Sort reports newest-first by creation date/time, so a pending (offline)
 * report interleaves chronologically instead of clumping into a type/status
 * group. Falls back to updatedAt when createdAt is missing (older local rows).
 */
function byCreatedDesc(list: Informe[]): Informe[] {
  const key = (i: Informe): string => i.createdAt || i.updatedAt || "";
  return [...list].sort((a, b) => key(b).localeCompare(key(a)));
}

export function TechnicianPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [filtros, setFiltros] = useState<InformeFiltros>({});
  const [busqueda, setBusqueda] = useState("");
  const [informes, setInformes] = useState<Informe[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enLinea, setEnLinea] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [eliminandoId, setEliminandoId] = useState<string | null>(null);
  /** IDs of reports that are local-only (no serverId — shown as "Pendiente" when en_cola). */
  const [localOnlyIds, setLocalOnlyIds] = useState<Set<string>>(new Set());
  /** Informe objetivo del modal de borrado; null = modal cerrado. */
  const [aEliminar, setAEliminar] = useState<Informe | null>(null);
  /** Bumped on reconnect to re-fetch the list automatically (no manual reload). */
  const [reloadKey, setReloadKey] = useState(0);
  /** True while a manual "Sincronizar" flush is running. */
  const [sincronizando, setSincronizando] = useState(false);

  useEffect(() => {
    let vivo = true; // prevents stale setState after unmount
    const subir = () => {
      setEnLinea(true);
      // Re-cache catalogs so the offline clientes/marca data stays fresh.
      void prewarmCatalogs();
      // Fix 6: replace the blind 3-second reload with a reload triggered on flush
      // completion. The flush-on-reconnect hook (useFlushOnReconnect) also calls
      // flushRun; the single-flight lock ensures only one flush runs — the other
      // call returns immediately with a zero summary. Either way, both reloads fire.
      //
      // Immediate reload: shows reports from IDB right away (clears the offline error).
      setReloadKey((k) => k + 1);
      // Post-flush reload: picks up the reconciled "sincronizado" state after the
      // outbox has been pushed to the server, without a race or hard-coded timer.
      void flushRun("online")
        .then(() => {
          if (vivo) setReloadKey((k) => k + 1);
        })
        .catch(() => {
          // flush error — the immediate reload above already shows IDB state.
        });
    };
    const bajar = () => setEnLinea(false);
    window.addEventListener("online", subir);
    window.addEventListener("offline", bajar);
    return () => {
      vivo = false;
      window.removeEventListener("online", subir);
      window.removeEventListener("offline", bajar);
    };
  }, []);

  // S1: on mount, reset any inflight outbox items from a previous interrupted session.
  // Fix 3: also clean up ghost IDB records left by the old serverId derivation bug.
  // Bug 2 fix: clean orphan outbox items (for reports already sincronizado/enviado).
  // Fase 3: prune the pdfs store (LRU) so stale cached PDFs don't accumulate.
  // ponytail: crash recovery — called once at startup; S2 will also call it in useFlushOnReconnect.
  useEffect(() => {
    let vivo = true;
    void resetInflight();
    void cleanupGhostReports(); // Fix 3: one-time cleanup of ghost records, best-effort
    // Bug 2 fix: remove orphan outbox entries whose report is already sincronizado/enviado
    // (e.g. crash between putReport(sincronizado) and outbox.remove() in reconcile).
    // If any were found, bump reloadKey so SyncStatusPanel immediately shows 0.
    void cleanupOrphanOutboxItems().then((n) => {
      if (n > 0 && vivo) setReloadKey((k) => k + 1);
    });
    // Recover reports stuck en_cola with no outbox entry (app closed between the
    // report write and the outbox write). Rebuild the outbox item, then flush if
    // online so they sync immediately instead of sitting "Pendiente" forever.
    void requeueOrphanEnColaReports().then((n) => {
      if (n > 0 && navigator.onLine) void flushRun("start");
      if (n > 0 && vivo) setReloadKey((k) => k + 1);
    });
    void pruneOnStartup(); // Fase 3: LRU prune of pdfs store — best-effort, never throws
    // Prewarm clientes + marca into IDB so the técnico has the same data offline.
    void prewarmCatalogs();
    return () => {
      vivo = false;
    };
  }, []);

  // Bug 2 fix: subscribe to flush completions so SyncStatusPanel re-reads the outbox
  // after ANY flush — including auto-flushes triggered by AuthGuard's useFlushOnReconnect
  // (cold-start, online event, visibility change). Without this, the panel could show
  // a stale "1 pendiente" after a startup flush cleared the outbox, because
  // TechnicianPanel's reloadKey was never bumped from outside its own event handlers.
  useEffect(() => {
    return subscribeToFlush(() => {
      setReloadKey((k) => k + 1);
    });
  }, []);

  useEffect(() => {
    let activo = true;
    setCargando(true);
    setError(null);

    async function cargar() {
      // S1: local-first read — load from IDB first, then merge/replace with network data.
      // Local records are authoritative for offline-only reports (no serverId yet).
      const localRecords = await getAllReports();

      // Map ALL local records (synced and not), keyed by serverId when present so a
      // synced report opened offline still resolves. This is what keeps the list
      // visible without a network call.
      const allLocal: Informe[] = localRecords.map((r) => ({
        id: r.serverId ?? r.localId,
        tipo: r.tipo,
        numero: r.numero ?? "(pendiente)",
        cliente: r.cliente ?? "",
        clienteId: r.clienteId ?? null,
        estado: r.estado,
        autorId: "",
        parentId: r.parentLocalId ?? null,
        payload: r.payload as Record<string, unknown>,
        pdfUrl: null,
        createdAt: r.createdAt ?? r.updatedAt,
        updatedAt: r.updatedAt,
      }));

      // Local-only = no serverId yet (shown as "Pendiente"), keyed by the displayed id.
      const localOnly = new Set(localRecords.filter((r) => !r.serverId).map((r) => r.localId));

      // Seed display from IDB immediately — keeps reports visible offline.
      if (activo && allLocal.length > 0) {
        setInformes(allLocal);
        setLocalOnlyIds(localOnly);
      }

      // Incremental sync: pass ?since when we have a last-sync timestamp AND no
      // active tipo/estado filters (filtered requests are always full fetches).
      // ponytail: skip since when filters active — partial filtered response would
      // exclude older matching records and shrink the visible list.
      const hasActiveFilters = !!(filtros.tipo || filtros.estado);
      const lastSync = hasActiveFilters ? null : getLastSync();

      // Then try network — replaces local view when online.
      try {
        const serverData = await listInformes({
          ...filtros,
          ...(lastSync ? { since: lastSync } : {}),
        });
        if (!activo) return;

        // Bug A fix: cache server reports into IDB so they survive going offline.
        // mirrorServerReports guards against overwriting pending local edits
        // (en_cola/borrador) — unsynced local work always wins.
        await mirrorServerReports(serverData);

        // Fase 2b: prefetch own PDFs so the technician can view/download them offline
        // without having to open each report first. Best-effort + bounded (RECENT_MAX=10).
        // Fire-and-forget — does NOT block UI update or error handling.
        void prefetchOwnPdfs(serverData);

        // Advance lastSync to max(updatedAt) from server response (server-authoritative).
        // Empty response → leave lastSync unchanged.
        if (serverData.length > 0) {
          const maxUpdatedAt = serverData.reduce(
            (max, r) => (r.updatedAt > max ? r.updatedAt : max),
            "",
          );
          setLastSync(maxUpdatedAt);
        }

        if (lastSync) {
          // Incremental path: re-read FULL IDB after mirror so the list never
          // shrinks (server returned only changed records; IDB has all of them).
          const refreshed = await getAllReports();
          if (!activo) return;
          const allRefreshed: Informe[] = refreshed.map((r) => ({
            id: r.serverId ?? r.localId,
            tipo: r.tipo,
            numero: r.numero ?? "(pendiente)",
            cliente: r.cliente ?? "",
            clienteId: r.clienteId ?? null,
            estado: r.estado,
            autorId: "",
            parentId: r.parentLocalId ?? null,
            payload: r.payload as Record<string, unknown>,
            pdfUrl: null,
            createdAt: r.updatedAt,
            updatedAt: r.updatedAt,
          }));
          const refreshedLocalOnly = new Set(
            refreshed.filter((r) => !r.serverId).map((r) => r.localId),
          );
          const pendingFromRefreshed = allRefreshed.filter((l) => refreshedLocalOnly.has(l.id));
          const syncedFromIDB = allRefreshed.filter((l) => !refreshedLocalOnly.has(l.id));
          setLocalOnlyIds(refreshedLocalOnly);
          setInformes(byCreatedDesc([...pendingFromRefreshed, ...syncedFromIDB]));
        } else {
          // Full-fetch path (first load OR filters active): render from server response.
          const serverIds = new Set(serverData.map((i) => i.id));
          const pendingLocal = allLocal.filter((l) => localOnly.has(l.id) && !serverIds.has(l.id));
          setLocalOnlyIds(new Set(pendingLocal.map((l) => l.id)));
          setInformes(byCreatedDesc([...pendingLocal, ...serverData]));
        }
        setError(null);
      } catch (err: unknown) {
        if (!activo) return;
        if (allLocal.length > 0) {
          // Offline — keep the full IDB view (synced reports included), no error banner.
          // Do NOT advance lastSync on network failure.
          setError(null);
        } else {
          setError(friendlyMessage(err));
        }
      } finally {
        if (activo) setCargando(false);
      }
    }

    void cargar();
    return () => {
      activo = false;
    };
  }, [filtros, reloadKey]);

  const enCola = useMemo(() => informes.filter((i) => i.estado === "en_cola").length, [informes]);

  /** Manual flush: push the outbox to the server now, then refresh the list. */
  async function sincronizarAhora() {
    if (sincronizando) return;
    setSincronizando(true);
    try {
      // Fase 3: reset terminal (failed) items so a manual retry gets a fresh chance.
      await resetFailedItems();
      // Manual tap = "try now": clear the backoff window on error items so they retry
      // this run instead of reporting "no había pendientes" while a 30s–5min delay elapses.
      await resetErrorBackoff();
      // Recover any en_cola report whose outbox entry was lost (crash between the two
      // writes) so a manual sync heals it too, without needing an app reload.
      await requeueOrphanEnColaReports();
      const { synced, skipped, errored, blobBlocked } = await flushRun("manual");
      setReloadKey((k) => k + 1);
      // Report honestly: a report can stay queued (network/blob error). Don't claim
      // success when nothing synced or something is still stuck.
      const stuck = errored + blobBlocked;
      if (stuck > 0) {
        toast.error(
          `${stuck} informe(s) no se pudieron sincronizar y siguen en cola. Revisa la conexión e intenta de nuevo.`,
        );
      } else if (synced + skipped === 0) {
        toast.info("No había informes pendientes para sincronizar.");
      } else {
        toast.success(`Sincronización completada — ${synced + skipped} informe(s).`);
      }
    } catch (err) {
      toast.error(friendlyMessage(err));
    } finally {
      setSincronizando(false);
    }
  }

  /** Discard reports the server keeps rejecting (terminal failed). Destructive — confirm first. */
  async function limpiarFallidos() {
    const ok = window.confirm(
      "Se borrarán los informes que no se pudieron sincronizar tras varios intentos. Esta acción no se puede deshacer. ¿Continuar?",
    );
    if (!ok) return;
    const n = await discardTerminalFailed();
    setReloadKey((k) => k + 1);
    toast.success(n > 0 ? `${n} informe(s) descartado(s).` : "No había informes para limpiar.");
  }

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return informes;
    return informes.filter(
      (i) => i.numero.toLowerCase().includes(q) || i.cliente.toLowerCase().includes(q),
    );
  }, [informes, busqueda]);

  async function confirmarEliminar() {
    const inf = aEliminar;
    if (!inf) return;
    setEliminandoId(inf.id);
    setError(null);
    try {
      if (localOnlyIds.has(inf.id)) {
        // Local-only borrador (never synced) → remove from IDB only; the server has no record.
        await deleteReport(inf.id);
      } else {
        // Server-backed → delete on the server, then drop any local cache copy.
        await deleteInforme(inf.id);
        const local = await getReportByServerId(inf.id);
        if (local) await deleteReport(local.localId);
      }
      setInformes((prev) => prev.filter((i) => i.id !== inf.id));
      toast.success("Informe eliminado");
      setAEliminar(null);
    } catch (err) {
      toast.error(friendlyMessage(err));
    } finally {
      setEliminandoId(null);
    }
  }

  return (
    <AppShell
      nav={[
        { icon: FileText, label: "Informes", active: true },
        { icon: User, label: "Mi perfil", onClick: () => navigate("/perfil") },
        { icon: HelpCircle, label: "Manual", onClick: () => navigate("/manual") },
      ]}
      footer={
        <button type="button" aria-label="Mi perfil" onClick={() => navigate("/perfil")}>
          <Avatar name={user?.nombre ?? "?"} />
        </button>
      }
    >
      {/* ── Barra de cabecera editorial ─────────────────────────────── */}
      <header className="sticky top-0 z-10 border-b border-border bg-card">
        <div className="flex items-center justify-between px-6 py-4 md:px-8">
          <div className="flex items-center gap-4">
            <BrandLogo height={32} className="max-w-[160px] object-contain" />
            <span aria-hidden className="hidden h-8 w-px bg-border sm:block" />
            <span className={cn(labelClass, "hidden sm:block")}>Reportes Técnicos de Campo</span>
          </div>
          <div className="flex items-center gap-5">
            <div className="text-right">
              <div className="text-sm font-semibold text-foreground">{user?.nombre}</div>
              <div className={labelClass}>Técnico</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="gap-2 rounded-full font-mono text-xs uppercase tracking-[0.12em]"
            >
              <LogOut className="size-4" aria-hidden />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1440px] space-y-10 px-6 py-8 md:px-8">
        {/* ── Bloque de título ───────────────────────────────────────────── */}
        <div
          className="forja-rise flex flex-wrap items-start justify-between gap-4"
          style={{ animationDelay: "60ms" }}
        >
          <div className="space-y-2">
            <p className={labelClass}>Técnico de campo</p>
            <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
              ¿Qué levantamos{" "}
              <span className="relative -top-0.5 inline-block -rotate-2 rounded-full bg-brand-accent px-3 py-0.5 italic font-medium text-brand-accent-foreground">
                hoy?
              </span>
            </h1>
            <p className="max-w-xl text-sm text-muted-foreground">
              Elige el tipo de informe. Funciona offline — se sincroniza al recuperar señal.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[0.625rem] font-medium uppercase tracking-[0.12em]",
                enLinea
                  ? "border-border text-muted-foreground"
                  : "border-destructive/40 text-destructive",
              )}
            >
              {enLinea ? (
                <Wifi className="size-3" aria-hidden />
              ) : (
                <WifiOff className="size-3" aria-hidden />
              )}
              {enLinea ? "En línea" : "Sin señal"}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 font-mono text-[0.625rem] font-medium uppercase tracking-[0.12em] text-muted-foreground">
              <span
                aria-hidden
                className="size-1.5 rounded-full"
                style={{ backgroundColor: "var(--estado-en-cola)" }}
              />
              En cola: {enCola}
            </span>
            {/* Manual sync — visible when online and there is something queued. */}
            {enLinea && enCola > 0 && (
              <Button
                variant="outline"
                size="sm"
                disabled={sincronizando}
                onClick={() => void sincronizarAhora()}
                className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.12em]"
              >
                {sincronizando ? (
                  <Loader2 className="size-3 animate-spin" aria-hidden />
                ) : (
                  <RefreshCw className="size-3" aria-hidden />
                )}
                Sincronizar
              </Button>
            )}
          </div>
        </div>

        {/* ── Panel de sincronización (pendientes + errores + reintento) ──── */}
        <SyncStatusPanel
          online={enLinea}
          sincronizando={sincronizando}
          onSincronizar={() => void sincronizarAhora()}
          onLimpiar={() => void limpiarFallidos()}
          reloadKey={reloadKey}
        />

        {/* ── Selector de tipo de informe ────────────────────────────────── */}
        <section className="space-y-4">
          <div className="space-y-1">
            <p className={labelClass}>Nuevo informe</p>
            <h2 className="font-display text-xl font-semibold tracking-tight text-foreground">
              ¿Qué levantamos <span className="italic font-medium">hoy?</span>
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TIPOS.map((t, i) => {
              // IS = recommended → highlighted card (brand border + corner glow + solid CTA).
              const destacado = t === "IS";
              return (
                <div
                  key={t}
                  className={cn(
                    "forja-rise group relative flex flex-col gap-4 overflow-hidden rounded-3xl border bg-card p-6 transition-all hover:-translate-y-0.5 hover:shadow-sm",
                    destacado ? "border-2 border-brand-accent" : "border-border",
                  )}
                  style={{ animationDelay: `${120 + i * 60}ms` }}
                >
                  {destacado && (
                    <span
                      aria-hidden
                      className="pointer-events-none absolute -right-8 -top-8 size-28 rounded-full bg-brand-accent/10"
                    />
                  )}
                  <div className="flex items-center justify-between">
                    <TipoBadge tipo={t} />
                  </div>
                  <div className="space-y-1.5">
                    <h3 className="font-display text-base font-semibold tracking-tight text-foreground">
                      {TIPO_LABEL[t]}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{TIPO_DESC[t]}</p>
                  </div>
                  <Link
                    to={`/informe/nuevo?tipo=${t}`}
                    className={cn(
                      buttonVariants({ variant: destacado ? "default" : "outline", size: "sm" }),
                      "mt-auto w-full justify-center gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.12em]",
                      destacado &&
                        "bg-brand-accent text-brand-accent-foreground hover:bg-brand-accent/90",
                    )}
                  >
                    Comenzar →
                  </Link>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Mis informes ───────────────────────────────────────────────── */}
        <section className="forja-rise space-y-4" style={{ animationDelay: "300ms" }}>
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className={labelClass}>Historial</p>
              <h2 className="font-display text-xl font-semibold tracking-tight text-foreground">
                Mis <span className="italic font-medium">informes</span>
              </h2>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 text-muted-foreground hover:text-foreground"
                  aria-label="¿Qué significa cada estado?"
                >
                  <HelpCircle className="size-4" aria-hidden />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 max-w-[calc(100vw-2rem)]">
                <p className={cn(labelClass, "mb-3")}>Estados del informe</p>
                <ul className="space-y-3">
                  {ESTADOS.map((s) => (
                    <li key={s} className="flex gap-2.5">
                      <span
                        aria-hidden
                        className="mt-1.5 size-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: ESTADO_DOT[s] }}
                      />
                      <div className="space-y-0.5">
                        <p className="text-xs font-semibold text-foreground">{ESTADO_LABEL[s]}</p>
                        <p className="text-xs leading-relaxed text-muted-foreground">
                          {ESTADO_AYUDA[s]}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </PopoverContent>
            </Popover>
          </div>

          {/* ── Filtros editoriales ──────────────────────────────────────── */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="grid min-w-56 flex-1 gap-1.5">
              <label htmlFor="tec-busqueda" className={labelClass}>
                Buscar
              </label>
              <div className="relative">
                <Search
                  aria-hidden
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="tec-busqueda"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Número o cliente…"
                  className="bg-card pl-10"
                />
              </div>
            </div>
            <div className="grid gap-1.5 md:w-fit w-full">
              <label className={labelClass}>Tipo</label>
              <Select
                value={filtros.tipo ?? ""}
                onValueChange={(v) =>
                  setFiltros((f) => ({ ...f, tipo: (v || undefined) as TipoInforme | undefined }))
                }
              >
                <SelectTrigger className="md:w-44 flex-1">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todos</SelectItem>
                  {TIPOS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t} · {TIPO_LABEL[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5  md:w-fit w-full">
              <label className={labelClass}>Estado</label>
              <Select
                value={filtros.estado ?? ""}
                onValueChange={(v) =>
                  setFiltros((f) => ({
                    ...f,
                    estado: (v || undefined) as EstadoInforme | undefined,
                  }))
                }
              >
                <SelectTrigger className="md:w-44 flex-1">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todos</SelectItem>
                  {ESTADOS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {ESTADO_LABEL[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {/* ── Tabla + barra lateral de recursos ──────────────────────────── */}
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="overflow-hidden rounded-3xl border border-border bg-card lg:col-span-3">
              {cargando ? (
                <p className="p-6 text-sm text-muted-foreground">Cargando informes…</p>
              ) : error ? (
                <p className="p-6 text-sm text-destructive">{error}</p>
              ) : informes.length === 0 ? (
                <p className="p-6 text-sm text-muted-foreground">
                  No tienes informes todavía. Empieza uno desde «¿Qué levantamos hoy?».
                </p>
              ) : visibles.length === 0 ? (
                <p className="p-6 text-sm text-muted-foreground">
                  Sin resultados para «{busqueda}».
                </p>
              ) : (
                <>
                  {/* Móvil: lista de tarjetas */}
                  <ul className="divide-y divide-border md:hidden">
                    {visibles.map((inf) => (
                      <li key={inf.id} className="flex items-start gap-3 p-4">
                        <TipoBadge tipo={inf.tipo} />
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-xs text-foreground">{inf.numero}</span>
                            {resolveDisplayEstado(inf.estado, localOnlyIds.has(inf.id)) ===
                            "Pendiente" ? (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--estado-en-cola-bg)] px-2.5 py-0.5 text-[0.6875rem] font-medium text-[var(--estado-en-cola-text)]">
                                <span
                                  aria-hidden
                                  className="size-1.5 rounded-full"
                                  style={{ backgroundColor: "var(--estado-en-cola)" }}
                                />
                                Pendiente
                              </span>
                            ) : (
                              <StatusBadge estado={inf.estado} />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Avatar name={inf.cliente} />
                            <span className="truncate text-sm text-foreground">{inf.cliente}</span>
                          </div>
                          <p className="font-mono text-xs text-muted-foreground">
                            {formatFecha(inf.updatedAt)}
                          </p>
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {inf.estado === "borrador" && (
                              <Link
                                to={`/informe/${inf.id}`}
                                className={cn(
                                  buttonVariants({ variant: "outline", size: "sm" }),
                                  accionClass,
                                )}
                              >
                                <Pencil className="size-3" aria-hidden />
                                Continuar
                              </Link>
                            )}
                            {inf.estado !== "borrador" && (
                              <Link
                                to={`/informe/${inf.id}/detalle`}
                                className={cn(
                                  buttonVariants({ variant: "outline", size: "sm" }),
                                  accionClass,
                                )}
                              >
                                <FileText className="size-3" aria-hidden />
                                Ver detalle
                              </Link>
                            )}
                            {inf.estado === "borrador" && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={eliminandoId === inf.id}
                                onClick={() => setAEliminar(inf)}
                                className={cn(
                                  accionClass,
                                  "border-destructive/40 text-destructive hover:bg-destructive/10",
                                )}
                              >
                                {eliminandoId === inf.id ? (
                                  <Loader2 className="size-3 animate-spin" aria-hidden />
                                ) : (
                                  <Trash2 className="size-3" aria-hidden />
                                )}
                                Eliminar
                              </Button>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  {/* Desktop: tabla */}
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full min-w-[36rem] text-sm">
                      <thead className="border-b border-border bg-secondary/50 text-left text-muted-foreground">
                        <tr>
                          <th className={thClass}>Número</th>
                          <th className={thClass}>Cliente</th>
                          <th className={thClass}>Estado</th>
                          <th className={thClass}>Actualizado</th>
                          <th className={cn(thClass, "text-center")}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibles.map((inf) => (
                          <tr
                            key={inf.id}
                            className="border-b border-border last:border-0 hover:bg-secondary/40"
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2.5">
                                <TipoBadge tipo={inf.tipo} />
                                <span className="font-mono text-xs text-foreground">
                                  {inf.numero}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2.5">
                                <Avatar name={inf.cliente} />
                                <span className="text-foreground">{inf.cliente}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {/* E-T4: show "Pendiente" for local-only en_cola reports (S-11.1). */}
                              {resolveDisplayEstado(inf.estado, localOnlyIds.has(inf.id)) ===
                              "Pendiente" ? (
                                <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[0.6875rem] font-medium bg-[var(--estado-en-cola-bg)] text-[var(--estado-en-cola-text)]">
                                  <span
                                    aria-hidden
                                    className="size-1.5 rounded-full"
                                    style={{ backgroundColor: "var(--estado-en-cola)" }}
                                  />
                                  Pendiente
                                </span>
                              ) : (
                                <StatusBadge estado={inf.estado} />
                              )}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                              {formatFecha(inf.updatedAt)}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap items-center justify-end gap-1.5">
                                {/* ponytail: PDF, Enviar, Crear IS/AC live in the detail page now.
                              Panel keeps only lifecycle: edit borrador, navigate to detail, delete. */}

                                {/* borrador: Continuar (editor editable) */}
                                {inf.estado === "borrador" && (
                                  <Link
                                    to={`/informe/${inf.id}`}
                                    className={cn(
                                      buttonVariants({ variant: "outline", size: "sm" }),
                                      accionClass,
                                    )}
                                  >
                                    <Pencil className="size-3" aria-hidden />
                                    Continuar
                                  </Link>
                                )}

                                {/* Ver detalle — visible for all non-borrador reports */}
                                {inf.estado !== "borrador" && (
                                  <Link
                                    to={`/informe/${inf.id}/detalle`}
                                    className={cn(
                                      buttonVariants({ variant: "outline", size: "sm" }),
                                      accionClass,
                                    )}
                                  >
                                    <FileText className="size-3" aria-hidden />
                                    Ver detalle
                                  </Link>
                                )}

                                {/* Eliminar — solo borrador */}
                                {inf.estado === "borrador" && (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={eliminandoId === inf.id}
                                    onClick={() => setAEliminar(inf)}
                                    className={cn(
                                      accionClass,
                                      "border-destructive/40 text-destructive hover:bg-destructive/10",
                                    )}
                                  >
                                    {eliminandoId === inf.id ? (
                                      <Loader2 className="size-3 animate-spin" aria-hidden />
                                    ) : (
                                      <Trash2 className="size-3" aria-hidden />
                                    )}
                                    Eliminar
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* ── Confirmación de borrado (modal único controlado) ──────────────── */}
      <AlertDialog
        open={aEliminar !== null}
        onOpenChange={(abierto) => {
          if (!abierto && eliminandoId === null) setAEliminar(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar borrador?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a eliminar {aEliminar?.numero} — {aEliminar?.cliente}. Esta acción no se puede
              deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={eliminandoId !== null}
              className="font-mono text-[0.625rem] uppercase tracking-[0.1em]"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={eliminandoId !== null}
              onClick={(e) => {
                e.preventDefault();
                void confirmarEliminar();
              }}
              className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em]"
            >
              {eliminandoId !== null ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
              ) : (
                <Trash2 className="size-3.5" aria-hidden />
              )}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
